import { useTranslation } from 'react-i18next'

import { useGetOrganisationSettingsDetails } from '@/modules/admin-center/organisation-settings/details/hooks/useGetOrganisationSettingsDetails'
import { formatAddress } from '@/modules/requisitions/utils/formatAddress'
import { Fallback } from '@/modules/shared/components/fallback/Fallback'
import { TableRowSingle } from '@/modules/shared/components/page-header-details-table/TableRowSingle'
import { PPNewDetailLink } from '@/modules/shared/components/ppnew-detail-link/PPNewDetailLink'
import { QueryResult } from '@/modules/shared/components/query-result/QueryResult'
import { BooleanItem } from '@/modules/shared/components/table/table-column-items/shared/BooleanItem'
import { Dates } from '@/modules/shared/types/Dates'
import { formatDate } from '@/modules/shared/utils/formatDate'

export default function OrganisationSettingsDetails() {
  const { t } = useTranslation()
  const { organisation, loadingStates, error } = useGetOrganisationSettingsDetails()
  const {
    id,
    name,
    isAPurchaser,
    parent,
    billingAddressCountry,
    createdAt,
    owner,
    isVerified,
    legalEntity,
    businessName,
    businessNumber,
    billingAddressLine1,
    billingAddressLine2,
    billingAddressCity,
    billingAddressStateProvince,
    billingAddressPostalCode,
    postalAddressLine1,
    postalAddressLine2,
    postalAddressCity,
    postalAddressStateProvince,
    postalAddressPostalCode,
  } = organisation || {}

  return (
    <section className="flex items-center justify-between rounded-md bg-white p-4 text-sm shadow">
      <QueryResult loading={loadingStates.loading} error={error}>
        {organisation && (
          <div className="w-full divide-y divide-gray-200 overflow-auto rounded-md border border-gray-300">
            <TableRowSingle
              titleText={t('general.organisationName', 'Organisation Name')}
              tooltipText={t('organisationDetails.organisationNameTooltip', 'The name of the Organisation.')}
              testId="organisationName"
              value={name}
            />
            <TableRowSingle
              titleText={t('general.organisationType', 'Organisation Type')}
              tooltipText={t(
                'organisationDetails.organisationTypeTooltip',
                'Indicates if the Organisation is a Purchaser or a Supplier.'
              )}
              testId="organisationType"
              value={isAPurchaser ? t('common.role.purchaser', 'Purchaser') : t('general.supplier', 'Supplier')}
            />
            <TableRowSingle
              titleText={t('general.organisationId', 'Organisation ID')}
              tooltipText={t(
                'organisationDetails.organisationIdTooltip',
                'The unique identifier for this Organisation in PurchasePlus.'
              )}
              testId="organisationId"
              value={id}
            />
            <TableRowSingle
              titleText={t('general.parentOrganisation', 'Parent Organisation')}
              tooltipText={
                isAPurchaser
                  ? t(
                      'organisationDetails.parentOrganisationPurchaserTooltip',
                      'The name of the direct parent of the Purchaser Organisation, if any. Purchasing Organisations are often nested beneath another Organisation in a hierarchy.'
                    )
                  : t(
                      'organisationDetails.parentOrganisationSupplierTooltip',
                      'The name of the direct parent of the Supplier Organisation, if any. Non-Verified Purchaser Managed Suppliers may sometimes be nested beneath the Verified Supplier.'
                    )
              }
              testId="parentOrganisation"
              value={
                <Fallback condition={!!parent}>
                  <PPNewDetailLink type="utility-organisation" id={String(parent?.id)}>
                    {parent?.name}
                  </PPNewDetailLink>
                </Fallback>
              }
            />
            <TableRowSingle
              titleText={t('general.country', 'Country')}
              tooltipText={t('organisationDetails.countryTooltip', 'The country that the Organisation is based in.')}
              testId="country"
              value={<Fallback condition={!!billingAddressCountry}>{billingAddressCountry}</Fallback>}
            />
            <TableRowSingle
              titleText={t('general.createdOn', 'Created On')}
              tooltipText={t(
                'organisationDetails.createdOnTooltip',
                'The time and date the Organisation was created in PurchasePlus.'
              )}
              testId="createdOn"
              value={<Fallback condition={!!createdAt}>{formatDate(Dates.Long, createdAt)}</Fallback>}
            />
            <TableRowSingle
              titleText={t('general.owner', 'Owner')}
              tooltipText={
                isAPurchaser
                  ? t(
                      'organisationDetails.ownerPurchaserTooltip',
                      'The name of the Organisation that owns this Organisation. For Purchaser Organisations, the concept of ownership does not apply. The Owner here should be PurchasePlus. Refer to the PurchasePlus Configuration and Implementation Handbook for more information.'
                    )
                  : t(
                      'organisationDetails.ownerSupplierTooltip',
                      'The name of the Organisation that owns this Organisation. This is generally only applicable where a Supplier is owned and managed by a Purchasing Organisation, and the Purchaser thinks of these as Self-Managed Suppliers. If the Organisation is not a Supplier being managed by a Purchaser, it is normal for the Owner to be PurchasePlus.'
                    )
              }
              testId="owner"
              value={
                <Fallback condition={!!owner}>
                  <PPNewDetailLink type="utility-organisation" id={String(owner?.id)}>
                    {owner?.name}
                  </PPNewDetailLink>
                </Fallback>
              }
            />
            <TableRowSingle
              titleText={t('general.verified', 'Verified')}
              tooltipText={
                isAPurchaser
                  ? t(
                      'organisationDetails.verifiedPurchaserTooltip',
                      'Indicates that the Organisation is a registered user of PurchasePlus. Typically all Purchaser Organisations will be set to Verified. Refer to the PurchasePlus Configuration and Implementation Handbook for more information.'
                    )
                  : t(
                      'organisationDetails.verifiedSupplierTooltip',
                      'Indicates that the Organisation is a registered user of PurchasePlus. A Non-Verified Supplier is typically a Supplier that is Self-Managed by a Purchaser.'
                    )
              }
              testId="isVerified"
              value={<BooleanItem value={!!isVerified} />}
            />
            <TableRowSingle
              titleText={t('general.legalEntity', 'Legal Entity')}
              tooltipText={
                isAPurchaser
                  ? t(
                      'organisationDetails.legalEntityPurchaserTooltip',
                      'Indicates that the Organisation is a Legal Entity. Typically a Legal Entity is the Organisation that Suppliers are connected to, Receive Purchase Orders from, and send Invoices to. A Non-Legal Entity is typically a department within the Organisation.'
                    )
                  : t(
                      'organisationDetails.legalEntitySupplierTooltip',
                      'Indicates that the Organisation is a Legal Entity. Typically a Legal Entity is the Organisation that Purchasers are connected to, Send Purchase Orders to, and receive Invoices from. A Non-Legal Entity is typically a department within the Organisation.'
                    )
              }
              testId="legalEntity"
              value={<BooleanItem value={!!legalEntity} />}
            />
            <TableRowSingle
              titleText={t('general.businessName', 'Business Name')}
              tooltipText={
                isAPurchaser
                  ? t(
                      'organisationDetails.businessNamePurchaserTooltip',
                      'The Business Name that applies to the Organisation. This may be different to the Organisation Name.'
                    )
                  : t(
                      'organisationDetails.businessNameSupplierTooltip',
                      'The Business Name that applies to the Supplier. This may be different to the Organisation Name.'
                    )
              }
              testId="businessName"
              value={<Fallback condition={!!businessName}>{businessName}</Fallback>}
            />
            <TableRowSingle
              titleText={t('general.businessNumber', 'Business Number')}
              tooltipText={
                isAPurchaser
                  ? t(
                      'organisationDetails.businessNumberPurchaserTooltip',
                      'The legal Business Number of the Purchaser.'
                    )
                  : t('organisationDetails.businessNumberSupplierTooltip', 'The legal Business Number of the Supplier.')
              }
              testId="businessNumber"
              value={<Fallback condition={!!businessNumber}>{businessNumber}</Fallback>}
            />
            <TableRowSingle
              titleText={t('general.billingAddress', 'Billing Address')}
              tooltipText={
                isAPurchaser
                  ? t('organisationDetails.billingAddressPurchaserTooltip', 'The Billing Address of the Purchaser.')
                  : t('organisationDetails.billingAddressSupplierTooltip', 'The Billing Address of the Supplier.')
              }
              testId="billingAddress"
              value={formatAddress({
                line1: billingAddressLine1 || '',
                line2: billingAddressLine2 || '',
                city: billingAddressCity || '',
                stateProvince: billingAddressStateProvince || '',
                postalCode: billingAddressPostalCode || '',
                locationName: '',
              })}
            />
            <TableRowSingle
              titleText={t('general.postalAddress', 'Postal Address')}
              tooltipText={
                isAPurchaser
                  ? t('organisationDetails.postalAddressPurchaserTooltip', 'The Postal Address of the Purchaser.')
                  : t('organisationDetails.postalAddressSupplierTooltip', 'The Postal Address of the Supplier.')
              }
              testId="postalAddress"
              value={formatAddress({
                line1: postalAddressLine1 || '',
                line2: postalAddressLine2 || '',
                city: postalAddressCity || '',
                stateProvince: postalAddressStateProvince || '',
                postalCode: postalAddressPostalCode || '',
                locationName: '',
              })}
            />
          </div>
        )}
      </QueryResult>
    </section>
  )
}
